import { useState, type FC } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import FormDialog from './FormDialog';
import Select from './Select';
import { TarefaService, type TipoTarefa } from '../services/TarefaService';

interface CriarTarefaDialogProps {
    /** Quem é o alvo da tarefa: a conversa (que é a oportunidade) e o nome para a pergunta. */
    conversaId: number;
    nomeContato: string;
    tipos: TipoTarefa[];
    onFechar: () => void;
}

const hojeISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * "Criar tarefa" do menu da conversa (docs/propostas/chat/menu-e-atalhos.html, fase 2): três campos —
 * o que fazer, de que tipo e para quando. A tarefa já nasce ligada à oportunidade da conversa e com
 * quem criou como responsável (a API assume). Quem monta este diálogo só o faz quando há tipo de
 * tarefa cadastrado: sem tipo não há como criar.
 */
const CriarTarefaDialog: FC<CriarTarefaDialogProps> = ({ conversaId, nomeContato, tipos, onFechar }) => {
    const [descricao, setDescricao] = useState('');
    const [tipo, setTipo] = useState(tipos[0] ? String(tipos[0].tipoTarefaId) : '');
    const [data, setData] = useState(hojeISO());
    const [hora, setHora] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const confirmar = async () => {
        const texto = descricao.trim();
        if (!texto) { setErro('Escreva o que precisa ser feito.'); return; }
        if (!tipo) { setErro('Escolha o tipo da tarefa.'); return; }
        if (!data) { setErro('Informe o prazo.'); return; }

        const [ano, mes, dia] = data.split('-').map(Number);
        const [h, m] = (hora || '09:00').split(':').map(Number);
        const prazo = new Date(ano, (mes || 1) - 1, dia || 1, h || 0, m || 0, 0, 0);

        setSalvando(true);
        try {
            const r = await TarefaService.criar({
                descricao: texto,
                conversaId,
                tipoTarefaId: Number(tipo),
                dataRetorno: prazo.toISOString(),
            });
            if (r.sucesso) {
                aviso.ok('Tarefa criada.');
                onFechar();
            } else setErro(r.mensagem || 'Não foi possível criar a tarefa.');
        } catch {
            setErro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <FormDialog
            open
            onClose={onFechar}
            titulo={`Criar tarefa para ${nomeContato}`}
            sub="Fica ligada à oportunidade desta conversa e aparece em Tarefas."
            confirmLabel="Criar tarefa"
            busyLabel="Criando…"
            busy={salvando}
            confirmDisabled={!descricao.trim() || !tipo || !data}
            onConfirm={confirmar}
        >
            <div className="ds-fd-campo">
                <label className="ds-fd-lab" htmlFor="ct-descricao">O que precisa ser feito</label>
                <input id="ct-descricao" className="ds-fd-inp" type="text" autoFocus
                    value={descricao} placeholder="Ex.: Ligar para confirmar o endereço"
                    onChange={e => { setDescricao(e.target.value); setErro(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') confirmar(); }} />
                {erro && <p className="ds-fd-hint" style={{ color: 'var(--fd-bad)' }}>{erro}</p>}
            </div>
            <div className="ds-fd-campo">
                <label className="ds-fd-lab">Tipo</label>
                <Select value={tipo} onChange={v => { setTipo(v); setErro(''); }}
                    placeholder="Escolher tipo" portal
                    options={tipos.map(t => ({ value: String(t.tipoTarefaId), label: t.nome }))} />
            </div>
            <div className="ds-fd-linha">
                <div className="ds-fd-campo">
                    <label className="ds-fd-lab" htmlFor="ct-data">Prazo</label>
                    <input id="ct-data" className="ds-fd-inp" type="date" value={data}
                        onChange={e => { setData(e.target.value); setErro(''); }} />
                </div>
                <div className="ds-fd-campo">
                    <label className="ds-fd-lab" htmlFor="ct-hora">Hora (opcional)</label>
                    <input id="ct-hora" className="ds-fd-inp" type="time" value={hora}
                        onChange={e => setHora(e.target.value)} />
                </div>
            </div>
        </FormDialog>
    );
};

export default CriarTarefaDialog;
